// Typed fetch wrappers for the admin user-management API.
//
// admin.rdx.tools is a SEPARATE Firebase Hosting site whose `/api/**` is
// rewritten to the SAME Cloud Run backend as decks.rdx.tools. Every call is
// same-origin and carries `Authorization: Bearer <Firebase ID token>`, minted
// from the firewall-safe auth the decks app already uses: the browser signed
// in against the OPEN `POST /api/auth/signin` (→ Firebase custom token →
// signInWithCustomToken → Firebase session), and `getToken()` (reused verbatim
// from ../src/lib/auth) returns the current Firebase ID token.
//
// The SERVER enforces admin on every `/api/admin/*` route — this module (and
// the FE gate) are UX, not the security boundary. Error bodies are shaped
// `{ "error": "<message>" }`; we surface that message verbatim and attach the
// HTTP status so callers can branch (e.g. 403 non-Ricoh vs 422 weak password).
//
// Mirrors the pattern of ../src/api/client.ts's httpAuth<T>() without importing
// any decks-specific deck/share methods.

import { getToken } from '../src/lib/auth';

/** A user as returned by the admin API. `lastSignInAt`/`createdAt` are epoch
 *  millis; `lastSignInAt === 0` means the account has never signed in. */
export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'member';
  emailVerified: boolean;
  banned: boolean;
  locked: boolean;
  /** Epoch ms; 0 = never signed in. */
  lastSignInAt: number;
  /** Epoch ms. */
  createdAt: number;
  imageUrl: string;
}

/** GET /api/admin/me — the signed-in caller's identity + role. */
export interface AdminMe {
  email: string;
  name: string;
  role: string;
}

/** A live, pending invitation as returned by the admin API. `createdAt` and
 *  `expiresAt` are RFC3339 timestamp strings. */
export interface Invitation {
  email: string;
  role: 'admin' | 'member';
  invitedBy: string;
  /** RFC3339 timestamp. */
  createdAt: string;
  /** RFC3339 timestamp. */
  expiresAt: string;
}

/** GET /api/admin/invitations — the live pending invitations, newest first. */
export interface InvitationList {
  invitations: Invitation[];
}

/** GET /api/admin/users — one page of results plus the unfiltered/filtered
 *  total used to drive pagination. */
export interface UserList {
  users: AdminUser[];
  total: number;
}

/** POST /api/admin/users request body. `name` is optional. */
export interface CreateUserRequest {
  email: string;
  name?: string;
  password: string;
  role: 'admin' | 'member';
}

/**
 * An HTTP error carrying the response status + the server's `{ error }` message
 * (already user-facing). Callers branch on `.status` (403 / 422 / 400 …) and
 * surface `.message` verbatim. Mirrors ../src/api/client.ts's HttpError but
 * keeps the server message rather than the bare statusText.
 */
export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/** In dev Vite proxies /api → :18080; in prod admin.rdx.tools rewrites /api/**
 *  to the same Cloud Run backend. An empty base prefix works either way. */
const BASE = '';

/**
 * Authenticated JSON request. Attaches `Authorization: Bearer <token>` when a
 * token is available, parses a 2xx JSON body (or returns undefined for 204),
 * and on a non-2xx throws an {@link ApiError} carrying the status and the
 * server's `{ error }` message (falling back to statusText when the body has
 * none — e.g. a 500 HTML page).
 */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init?.headers as Record<string, string> | undefined) ?? {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(BASE + path, { ...init, headers });

  if (!res.ok) {
    let message = res.statusText || `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string } | null;
      if (body?.error) message = body.error;
    } catch {
      /* non-JSON error body — keep the statusText fallback */
    }
    throw new ApiError(res.status, message);
  }

  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

/** Build the `/api/admin/users` query string from the search + paging state. */
function usersQuery(query: string, limit: number, offset: number): string {
  const p = new URLSearchParams();
  if (query.trim()) p.set('query', query.trim());
  p.set('limit', String(limit));
  p.set('offset', String(offset));
  return p.toString();
}

export const adminApi = {
  /** Confirm the caller is an admin + show who's signed in. */
  me: (): Promise<AdminMe> => request<AdminMe>('/api/admin/me'),

  /** List users with a search filter + pagination window. */
  listUsers: (query: string, limit: number, offset: number): Promise<UserList> =>
    request<UserList>(`/api/admin/users?${usersQuery(query, limit, offset)}`),

  /** Create a user. 201 → the new AdminUser. 403 non-Ricoh · 400 bad/missing ·
   *  422 weak password — each surfaces as an ApiError with the server message. */
  createUser: (body: CreateUserRequest): Promise<AdminUser> =>
    request<AdminUser>('/api/admin/users', { method: 'POST', body: JSON.stringify(body) }),

  /** Change a user's role. 400 on a bad role or a self-demote (server-guarded). */
  setRole: (id: string, role: 'admin' | 'member'): Promise<{ ok: true }> =>
    request<{ ok: true }>(`/api/admin/users/${encodeURIComponent(id)}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),

  /** Disable (ban) a user. 400 on a self-ban (server-guarded). */
  ban: (id: string): Promise<{ ok: true }> =>
    request<{ ok: true }>(`/api/admin/users/${encodeURIComponent(id)}/ban`, { method: 'POST' }),

  /** Re-enable (unban) a user. */
  unban: (id: string): Promise<{ ok: true }> =>
    request<{ ok: true }>(`/api/admin/users/${encodeURIComponent(id)}/unban`, { method: 'POST' }),

  /** Delete a user. 400 on a self-delete (server-guarded). */
  remove: (id: string): Promise<{ ok: true }> =>
    request<{ ok: true }>(`/api/admin/users/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /** List the live pending invitations (newest first). */
  listInvitations: (): Promise<InvitationList> =>
    request<InvitationList>('/api/admin/invitations'),

  /** Invite a user by email. 201 → ok. 403 non-Ricoh · 409 already a user ·
   *  400 bad/missing — each surfaces as an ApiError with the server message. */
  inviteUser: async (email: string, role: string): Promise<void> => {
    await request<{ ok: true }>('/api/admin/invitations', {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    });
  },

  /** Revoke a pending invitation by email. */
  revokeInvitation: async (email: string): Promise<void> => {
    await request<{ ok: true }>(`/api/admin/invitations/${encodeURIComponent(email)}`, {
      method: 'DELETE',
    });
  },
};
